/**
 * Carga inicial de catálogos.
 *
 * Los maestros salen del histórico real de las planillas: 338 partidas del
 * contrato, 20 capítulos, y los catálogos de clientes, productos, equipos,
 * personas, centros de costo y destinos agrupados por alias.
 *
 * Se ejecuta una sola vez; después los catálogos se mantienen desde la app.
 */

import semillaJson from './semilla.json';
import { COL } from './firebase';
import { guardarLote, listar } from './repositorio';
import { CONTRATO_INICIAL, guardarContrato } from './contrato';
import { metaNueva, uuidv7 } from './tiposDatos';
import type {
  DocCapitulo,
  DocCatalogo,
  DocCentroCosto,
  DocItemContrato,
  DocPlanta,
  DocProducto,
} from './tiposDatos';
import { parsearProducto, normalizarCodigo, familiaDe, generaRecargos } from '@/domain/producto';
import type { CodigoPlanta, Sector, UnidadItem } from '@/domain/tipos';

interface EntradaCatalogo {
  canonico: string;
  alias: string[];
  usos: number;
}

interface SemillaJson {
  capitulos: { numero: number; nombre: string; orden: number }[];
  items: {
    codigo: string;
    capituloNumero: number;
    descripcion: string;
    unidad: string;
    cantidadContratada: number;
    precioBaseUf: number;
    aplicaReajuste: boolean;
    esRecargo: boolean;
  }[];
  clientes: EntradaCatalogo[];
  equipos: EntradaCatalogo[];
  personas: EntradaCatalogo[];
  centrosCosto: EntradaCatalogo[];
  productos: EntradaCatalogo[];
  destinos: EntradaCatalogo[];
  gastosGenerales: {
    grupo: string;
    descripcion: string;
    unidad: string;
    cantidadContratada: number;
    precioUnitarioUf: number;
    orden: number;
  }[];
}

const S = semillaJson as SemillaJson;

/** Identificador estable a partir del código: la resiembra no duplica. */
function idDe(prefijo: string, codigo: string): string {
  const limpio = codigo
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 40);
  return `${prefijo}-${limpio || uuidv7().slice(0, 8)}`;
}

const PLANTAS: { codigo: CodigoPlanta; nombre: string; sector: Sector; min: number }[] = [
  { codigo: 'NV 17', nombre: 'Planta Nivel 17', sector: 'INT MINA', min: 2 },
  { codigo: 'KM 13', nombre: 'Planta Km 13,5', sector: 'SUPERFICIE', min: 5 },
  { codigo: 'VALLE', nombre: 'Suministro desde Valle', sector: 'VALLE', min: 5 },
];

function catalogoBase(
  prefijo: string,
  e: EntradaCatalogo,
  usuarioId: string | null,
): DocCatalogo {
  return {
    ...metaNueva(usuarioId),
    id: idDe(prefijo, e.canonico),
    codigo: e.canonico,
    nombre: e.canonico,
    alias: e.alias,
    activo: true,
  };
}

/** ¿Ya hay catálogos cargados? */
export async function yaSembrado(): Promise<boolean> {
  const plantas = await listar<DocPlanta>(COL.plantas);
  return plantas.length > 0;
}

export const COL_GASTOS_GENERALES = 'gastosGenerales';

export interface ResultadoSemilla {
  plantas: number;
  capitulos: number;
  items: number;
  clientes: number;
  productos: number;
  equipos: number;
  personas: number;
  centrosCosto: number;
  destinos: number;
  gastosGenerales: number;
  productosSinClasificar: number;
}

export async function sembrar(usuarioId: string | null = null): Promise<ResultadoSemilla> {
  // ── Plantas ─────────────────────────────────────────────────────────────
  const plantas: DocPlanta[] = PLANTAS.map((p) => ({
    ...metaNueva(usuarioId),
    id: idDe('pl', p.codigo),
    codigo: p.codigo,
    nombre: p.nombre,
    alias: [],
    activo: true,
    codigoPlanta: p.codigo,
    sector: p.sector,
    minCargaM3: p.min,
  }));
  await guardarLote(COL.plantas, plantas);

  // ── Capítulos ───────────────────────────────────────────────────────────
  const vistos = new Set<number>();
  const capitulos: DocCapitulo[] = [];
  for (const c of S.capitulos) {
    if (vistos.has(c.numero)) continue; // el Excel repite el 17
    vistos.add(c.numero);
    capitulos.push({
      ...metaNueva(usuarioId),
      id: `cap-${c.numero}`,
      numero: c.numero,
      nombre: c.nombre,
      orden: c.orden,
    });
  }
  await guardarLote(COL.capitulos, capitulos);

  // ── Partidas del contrato ───────────────────────────────────────────────
  const items: DocItemContrato[] = S.items.map((i) => ({
    ...metaNueva(usuarioId),
    id: `it-${i.codigo}`,
    codigo: i.codigo,
    capituloNumero: i.capituloNumero,
    descripcion: i.descripcion,
    unidad: i.unidad as UnidadItem,
    cantidadContratada: i.cantidadContratada,
    precioBaseUf: i.precioBaseUf,
    aplicaReajuste: i.aplicaReajuste,
    esRecargo: i.esRecargo,
    activo: true,
  }));
  await guardarLote(COL.itemsContrato, items);

  // ── Productos, con su parseo ────────────────────────────────────────────
  let sinClasificar = 0;
  const productos: DocProducto[] = S.productos.map((e) => {
    const p = parsearProducto(e.canonico);
    if (!p.ok) sinClasificar++;
    return {
      ...catalogoBase('pr', e, usuarioId),
      codigoNormalizado: normalizarCodigo(e.canonico),
      familia: familiaDe(e.canonico, p),
      grado: p.grado,
      mpa: p.mpa,
      pctConfianza: p.pctConfianza,
      diametroArido: p.diametroArido,
      cono: p.cono,
      extras: p.extras,
      parseOk: p.ok,
      generaRecargos: generaRecargos(e.canonico, p),
    };
  });
  await guardarLote(COL.productos, productos);

  // ── Centros de costo ────────────────────────────────────────────────────
  const centros: DocCentroCosto[] = S.centrosCosto.map((e) => {
    const c = e.canonico.toUpperCase();
    const tipo: DocCentroCosto['tipo'] = c.startsWith('VP')
      ? 'VENTA_PARTICULAR'
      : /^A[MI][\s-]?\d/.test(c)
        ? 'GRAFO_CODELCO'
        : c.startsWith('OC')
          ? 'OC'
          : c.startsWith('CC')
            ? 'CC'
            : 'OTRO';
    return { ...catalogoBase('cc', e, usuarioId), tipo, proyectoId: null };
  });
  await guardarLote(COL.centrosCosto, centros);

  // ── Resto de catálogos ──────────────────────────────────────────────────
  const clientes = S.clientes.map((e) => catalogoBase('cl', e, usuarioId));
  const equipos = S.equipos.map((e) => catalogoBase('eq', e, usuarioId));
  const personas = S.personas.map((e) => catalogoBase('pe', e, usuarioId));
  const destinos = S.destinos.map((e) => catalogoBase('de', e, usuarioId));

  await guardarLote(COL.clientes, clientes);
  await guardarLote(COL.equipos, equipos);
  await guardarLote(COL.personas, personas);
  await guardarLote(COL.destinos, destinos);

  // ── Gastos generales ────────────────────────────────────────────────────
  const gg = S.gastosGenerales.map((l) => ({
    ...metaNueva(usuarioId),
    id: `gg-${l.orden}`,
    ...l,
    activo: true,
  }));
  await guardarLote(COL_GASTOS_GENERALES, gg);

  // ── Contrato vigente ────────────────────────────────────────────────────
  // Los montos del contrato son un dato que se administra, no una constante
  // del programa: se siembran aquí y se editan desde Administración.
  await guardarContrato(CONTRATO_INICIAL, usuarioId);

  return {
    plantas: plantas.length,
    capitulos: capitulos.length,
    items: items.length,
    clientes: clientes.length,
    productos: productos.length,
    equipos: equipos.length,
    personas: personas.length,
    centrosCosto: centros.length,
    destinos: destinos.length,
    gastosGenerales: gg.length,
    productosSinClasificar: sinClasificar,
  };
}
